<template>
  <div class="sensors">
    <Loading v-if="loading || !chartLoaded" />
    <div class="sensors__header">
      <div class="container">
        <Dropdown
          class="sensors__header-dropdown"
          :selected-option="selectedSensors.selectedOption[0]"
          :options="selectedSensors.options"
          data-testid="selectSensorGroup"
          @select-option="selectSensors"
        />
        <Button
            v-if="groupSensors().length"
            class="checkAllTemperatures-button"
            variant="filled"
            :title="$t('checkAllTemperatures')"
            @click="handleCheckRequest"
        />
      </div>
      <div class="chooseDate">
        <div class="date-block">
          <div class="date-inputs">
            <Button
                class="week-nav-button"
                variant="bordered"
                :title="$t('prevWeek') || 'Prev week'"
                @click="prevWeek"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.startTime"
                name="startTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.endTime"
                name="endTime"
                @change="changeDate"
                @close-panel="closeTimePanel"
              />
            </div>
            <Button
                class="update-date-button"
                variant="filled"
                style="margin-left: 12px;"
                :title="$t('update')"
                :disabled="!isDateCorrect || !isSixMonthValid"
                @click="handleUpdateClick"
            />
            <Button
                v-if="canGoNextWeek"
                class="week-nav-button"
                variant="bordered"
                style="margin-left: 12px;"
                :title="$t('nextWeek') || 'Next week'"
                @click="nextWeek"
            />
          </div>
          <p v-if="!isSixMonthValid" class="date-error">
            {{ $t('errorSixMonths') || 'Range cannot exceed 6 months' }}
          </p>
          <p v-if="!isDateCorrect" class="date-error">
            {{ $t('errorDate') }}
          </p>
        </div>
      </div>
    </div>
    <SensorsMain
        v-if="pharmacy && selectedSensors.selectedOption?.length"
        :pharmacy="pharmacy"
        :group-view="true"
        :line-chart-key="lineChartKey"
        :data-line-chart="dataLineChart"
        :alarms-sensor="alarmsSensor"
        :is-date-correct="isDateCorrect"
        :selected-sensors-data="selectedSensorsData"
        :selected-option="selectedSensors.selectedOption"
        :temperature-sensor-stamp="temperatureSensorStamp"
        :date-range="dateRange"
        :all-sensor-stamps="allSensorStamps"
        @refresh-alarms="initAlarmsSensor"
    />
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import Loading from '@/views/Loading.vue';
import SensorsMain from '../SensorsMain/SensorsMain.vue';

import { format } from 'date-fns';

import {
  ChartDatasets,
  Sensor,
  TemperatureSensorStamp
} from '@/store/modules/pharmacy/types';
import { InputFileEvent, Option } from '@/types';
import {
  prevWeekWindow,
  nextWeekWindow,
  canShiftWeekForward
} from '@/utils/shiftDateRange';
import {
  getTemperatureSensorStampsRange,
  getSensorGroupTemperatures,
  getSensorGroupAlarms
} from '@/services/axiosService';

const transformationDate = new Date().setDate(new Date().getDate() - 3);

@Options({
  name: 'SensorGroups',
  components: {
    Loading,
    SensorsMain
  },
  data() {
    return {
      selectedSensors: {
        selectedOption: [],
        options: []
      },
      groupOptions: [
        { value: 'all', label: this.$t('all') },
        { value: 'room', label: this.$t('room') },
        { value: 'fridge', label: this.$t('fridge') }
      ],
      dateRange: {
        startTime: format(new Date(transformationDate), 'dd/MM/yyyy'),
        endTime: format(new Date(), 'dd/MM/yyyy')
      },
      alarmsSensor: [],
      lineChartKey: 0,
      activeSensor: null,
      dataLineChart: [],
      selectedSensorsData: [],
      loading: false,
      chartLoaded: false,
      checkedSensor: false,
      showTimePanel: false,
      allSensorStamps: [] as Array<{ sensor: Sensor; stamp: TemperatureSensorStamp | null }>
    };
  },
  created() {
    if (this.user) {
      this.show();
    }
  },
  // updated() {
  //   if (this.sensors) {
  //     this.selectedSensors.selectedOption = this.sensorsOptions;
  //   }
  // },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy', 'sensor', 'temperatureSensorStamp']),
    isDateCorrect() {
      if (this.isDatePresent) {
        const startTime = this.dateRange.startTime.split('/');
        const endTime = this.dateRange.endTime.split('/');

        const defaultParams = {
          ...this.dateRange,
          startTime: new Date(
            +startTime[2],
            startTime[1] - 1,
            +startTime[0]
          ).valueOf(),
          endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
        };

        return defaultParams.endTime >= defaultParams.startTime;
      }

      return true;
    },
    isDatePresent() {
      const startTime = this.dateRange.startTime.split('/');
      const endTime = this.dateRange.endTime.split('/');

      const defaultParams = {
        ...this.dateRange,
        startTime: new Date(
          +startTime[2],
          startTime[1] - 1,
          +startTime[0]
        ).valueOf(),
        endTime: new Date(+endTime[2], endTime[1] - 1, +endTime[0]).valueOf()
      };

      return defaultParams && defaultParams.startTime && defaultParams.endTime;
    },
    isSixMonthValid() {
      const startParts = this.dateRange.startTime.split('/');
      const endParts = this.dateRange.endTime.split('/');

      const start = new Date(+startParts[2], startParts[1] - 1, +startParts[0]);
      const end = new Date(+endParts[2], endParts[1] - 1, +endParts[0]);

      // Calculate difference in months roughly (183 days is 6 months)
      const diffInMs = end.getTime() - start.getTime();
      const sixMonthsInMs = 1000 * 60 * 60 * 24 * 183;

      return diffInMs <= sixMonthsInMs;
    },
    canGoNextWeek() {
      return canShiftWeekForward(this.dateRange);
    },
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPharmacy',
      'sendTemperatureSensorsStamp'
    ]),
    async show() {
      await this.loadSensors();
      this.initSensorsOptions();
      // if (this.pharmacy?.sensors?.length > 0) {
      //   const firstSensorId = this.pharmacy.sensors[0].id;
      //   await this.initTemperatureSensorStamp(firstSensorId);
      // }
    },
    async loadSensors() {
      this.loading = true;

      await this.getPharmacy(this.user.pharmacyId);

      this.loading = false;
    },
    initSensorsOptions() {
      if (this.pharmacy) {
        this.selectedSensors.options = this.groupOptions;

        // Default to the "All" group if nothing is selected yet
        if (
          !this.selectedSensors.selectedOption ||
          this.selectedSensors.selectedOption.length === 0
        ) {
          this.selectedSensors.selectedOption = [this.groupOptions[0]];
          this.activeSensor = null;
        }
      }
    },
    async initAlarmsSensor() {
      this.loading = true;

      const filter = this.selectedSensors.selectedOption?.[0]?.value || 'all';

      const startTime = this.dateRange.startTime.split('/');
      this.getTotalEndTime();

      const startTimestamp = new Date(
        +startTime[2],
        startTime[1] - 1,
        +startTime[0]
      ).valueOf();
      const endTimestamp = new Date(
        +this.totalEndTime[2],
        +this.totalEndTime[1] - 1,
        +this.totalEndTime[0],
        +this.totalEndTime[3],
        +this.totalEndTime[4],
        +this.totalEndTime[5]
      ).valueOf();

      if (startTimestamp && endTimestamp && this.isDateCorrect) {
        const response = await getSensorGroupAlarms(
          this.user.pharmacyId,
          filter,
          startTimestamp,
          endTimestamp
        );

        this.alarmsSensor = Array.isArray(response) ? response : [];
      }

      this.loading = false;
    },
    async handleCheckRequest() {
      // Check only the sensors that belong to the currently selected group
      // (all / room / fridge).
      const sensorSns = this.groupSensors().map((s: Sensor) => s.sn);
      await this.processCheckRequests(sensorSns);
    },
    // Collects the unique Max/Min threshold values configured on the given
    // sensors and turns each one into a single dashed horizontal line dataset.
    buildThresholdDatasets(sensors: Sensor[], labelsLength: number): ChartDatasets[] {
      const maxValues = new Set<number>();
      const minValues = new Set<number>();

      sensors.forEach((sensor: Sensor) => {
        if (typeof sensor.max === 'number' && !isNaN(sensor.max)) {
          maxValues.add(sensor.max);
        }
        if (typeof sensor.min === 'number' && !isNaN(sensor.min)) {
          minValues.add(sensor.min);
        }
      });

      const datasets: ChartDatasets[] = [];

      [...maxValues]
        .sort((a, b) => a - b)
        .forEach((value: number) => {
          datasets.push(
            this.buildThresholdDataset(`Max ${value}°C`, value, labelsLength)
          );
        });

      [...minValues]
        .sort((a, b) => a - b)
        .forEach((value: number) => {
          datasets.push(
            this.buildThresholdDataset(`Min ${value}°C`, value, labelsLength)
          );
        });

      return datasets;
    },
    buildThresholdDataset(label: string, value: number, length: number): ChartDatasets {
      return {
        label,
        value: label,
        data: new Array(length).fill(value),
        backgroundColor: '#1B242D',
        borderColor: '#1B242D',
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0,
        borderDash: [5, 5],
        fill: false,
        pointBorderColor: 'transparent',
        pointBackgroundColor: 'transparent',
        pointHoverBackgroundColor: 'transparent',
        pointHoverBorderColor: 'transparent'
      };
    },
    getRandomColorFromScale() {
      const colorScale = [
        '#E6194B', // Red
        '#3CB44B', // Green
        '#FFE119', // Yellow
        '#4363D8', // Blue
        '#F58231', // Orange
        '#911EB4', // Purple
        '#42D4F4', // Cyan
        '#F032E6', // Magenta
        '#BFEF45', // Lime
        '#FABEBE', // Pink
        '#469990', // Teal
        '#E6BEFF', // Lavender
        '#9A6324', // Brown
        '#FFFAC8', // Beige
        '#800000', // Maroon
        '#AAFFC3', // Mint
        '#808000', // Olive
        '#FFD8B1', // Apricot
        '#000075', // Navy
        '#A9A9A9'  // Grey
      ];

      const index = Math.floor(Math.random() * colorScale.length);
      return colorScale[index];
    },
    async loadSensor() {
      this.chartLoaded = false;
      this.loading = true;

      const filter = this.selectedSensors.selectedOption?.[0]?.value || 'all';

      const startTime = this.dateRange.startTime.split('/');
      this.getTotalEndTime();

      const startTimestamp = new Date(
        +startTime[2],
        startTime[1] - 1,
        +startTime[0]
      ).valueOf();
      const endTimestamp = new Date(
        +this.totalEndTime[2],
        +this.totalEndTime[1] - 1,
        +this.totalEndTime[0],
        +this.totalEndTime[3],
        +this.totalEndTime[4],
        +this.totalEndTime[5]
      ).valueOf();

      if (this.isDateCorrect) {
        const response = await getSensorGroupTemperatures(
          this.user.pharmacyId,
          filter,
          startTimestamp,
          endTimestamp
        );

        if (response) {
          const cloned = JSON.parse(JSON.stringify(response));
          cloned.id = 'all';

          // One coloured temperature line per sensor (drop any Max/Min lines the
          // backend may send — thresholds are derived from pharmacy info below).
          const temperatureDatasets = (cloned.datasets || [])
            .filter((ds: ChartDatasets) => {
              const label = (ds.label || '').toUpperCase();
              return !label.includes('MAX') && !label.includes('MIN');
            })
            .map((ds: ChartDatasets) => {
              const matchedSensor = this.pharmacy?.sensors?.find(
                (s: Sensor) => s.sn === ds.value || s.id === ds.value
              );
              const color =
                matchedSensor?.color ||
                ds.borderColor ||
                this.getRandomColorFromScale();

              return {
                ...ds,
                borderColor: color,
                backgroundColor: color,
                pointBackgroundColor: color,
                pointBorderColor: color,
                // Thresholds used to paint out-of-range points/segments red.
                sensorMin: matchedSensor?.min,
                sensorMax: matchedSensor?.max
              };
            });

          // Unique Max/Min thresholds across the selected group's sensors,
          // drawn as dashed horizontal lines.
          const thresholdDatasets = this.buildThresholdDatasets(
            this.groupSensors(),
            cloned.labels?.length || 0
          );

          cloned.datasets = [...temperatureDatasets, ...thresholdDatasets];

          this.dataLineChart = cloned;
        } else {
          this.dataLineChart = { labels: [], datasets: [] };
        }

        this.lineChartKey++;
      }

      this.chartLoaded = true;
      this.loading = false;
    },
    getTotalEndTime() {
      const arrayEndTime = this.dateRange.endTime.split('/');
      const arrayToday = format(new Date(), 'dd/MM/yyyy/HH/mm/ss').split('/');
      // A past end date covers its whole day; only today stops at "now".
      const isToday =
        arrayEndTime.join('/') === arrayToday.slice(0, 3).join('/');
      const arrayHour = isToday ? arrayToday.slice(-3) : ['23', '59', '59'];

      this.totalEndTime = arrayEndTime.concat(arrayHour);
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;

      this.dateRange = {
        ...this.dateRange,
        [name]: value
      };
    },
    selectSensors(option: Option) {
      // Single-select group dropdown (all / room / fridge)
      if (!option) {
        this.selectedSensors.selectedOption = [this.groupOptions[0]];
        return;
      }

      this.selectedSensors.selectedOption = [option];
    },
    closeTimePanel() {
      this.showTimePanel = false;
    },
    async handleUpdateClick() {
      if (this.isDateCorrect && this.isSixMonthValid) {
        this.loading = true;
        await Promise.all([
          this.loadSensor(),
          this.initAlarmsSensor()
        ]);
        this.loading = false;
      }
    },
    async prevWeek() {
      this.dateRange = prevWeekWindow(this.dateRange);
      await this.handleUpdateClick();
    },
    async nextWeek() {
      if (!this.canGoNextWeek) return;
      this.dateRange = nextWeekWindow(this.dateRange);
      await this.handleUpdateClick();
    },
    async processCheckRequests(sns: string[]) {
      if (!sns.length) return;

      this.loading = true;
      try {
        for (const sn of sns) {
          await this.sendTemperatureSensorsStamp({ boxId: sn });
        }
        // Re-fetch the check stamps so the "last checked by" info updates
        // immediately, without needing a manual page reload.
        await this.initAllSensorsStamps();
      } catch (error) {
        console.error("Check failed", error);
      } finally {
        this.loading = false;
      }
    },
    // Returns the pharmacy sensors that belong to the currently selected
    // group (all sensors for "all", otherwise filtered by sensor type).
    groupSensors(): Sensor[] {
      const filter = this.selectedSensors.selectedOption?.[0]?.value || 'all';
      const sensors = this.pharmacy?.sensors || [];

      if (filter === 'all') return sensors;

      return sensors.filter((s: Sensor) => s.type === filter);
    },
    async initAllSensorsStamps() {
      const groupSensors = this.groupSensors();
      if (!groupSensors.length) {
        this.allSensorStamps = [];
        return;
      }

      const startTimeParts = this.dateRange.startTime.split('/');
      const endTimeParts = this.dateRange.endTime.split('/');
      const todayParts = format(new Date(), 'dd/MM/yyyy/HH/mm/ss').split('/');

      const startTime = new Date(
        +startTimeParts[2], startTimeParts[1] - 1, +startTimeParts[0]
      ).valueOf();
      const endTime = new Date(
        +endTimeParts[2], endTimeParts[1] - 1, +endTimeParts[0],
        +todayParts[3], +todayParts[4], +todayParts[5]
      ).valueOf();

      const results = await Promise.all(
        groupSensors.map(async (sensor: Sensor) => {
          const stamps: TemperatureSensorStamp[] | undefined =
            await getTemperatureSensorStampsRange(sensor.sn, startTime, endTime);
          const stamp = Array.isArray(stamps) && stamps.length > 0
            ? stamps.reduce((latest: TemperatureSensorStamp, s: TemperatureSensorStamp) =>
                s.timestamp > latest.timestamp ? s : latest
              )
            : null;
          return { sensor, stamp };
        })
      );

      this.allSensorStamps = results;
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.show();
      }
    },
    'selectedSensors.selectedOption': {
      immediate: true,
      handler(options: Option[]) {
        if (!Array.isArray(options) || !options.length) return;
        if (!this.pharmacy) return;

        this.selectedSensorsData = this.groupSensors();
        this.initAllSensorsStamps();

        this.loadSensor();
        this.initAlarmsSensor();
      }
    }
  }
})
export default class SensorGroups extends Vue {}
</script>

<style lang="scss" scoped>
.sensors {
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
  .sensors__placeholder {
    margin-top: 24px;
    padding: 40px;
    text-align: center;
    border: 1px dashed $border;
    border-radius: 8px;
    background-color: $light-grey;
    p {
      color: $mid-grey;
      font-size: $fourteenSize;
      margin: 0;
    }
  }
  .sensors__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 15px;
    padding: 24px 0;
    .container {
      display: flex;
      align-items: center;
      .sensors__header-dropdown {
        height: 40px;
        width: 250px !important;
      }
      .checkAllTemperatures-button {
        margin: 0 12px;
      }
    }
    .chooseDate {
      display: flex;
      height: 42px;
      .date-block {
        width: 100%;
        height: 100%;
        margin-left: 10px;
        .date-inputs {
          display: flex;
          align-items: center;
          .week-nav-button {
            white-space: nowrap;
          }
          .week-nav-button:first-child {
            margin-right: 12px;
          }
          .date-inputs-button {
            width: 60px;
            margin-right: 10px;
            z-index: 101;
            cursor: pointer;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            margin-bottom: 0 !important;
            .input-img {
              position: absolute;
              top: 12px;
              right: 12px;
              z-index: 0;
            }
          }
          .date-img {
            margin: 0 8px;
          }
        }
        .date-error {
          color: $red;
          font-size: $fourteenSize;
          margin-top: 10px;
        }
      }
    }
  }
}
</style>
